import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createAppError, type AppError, type ErrorContext as ErrorContextType, type ErrorRecoveryOptions } from '@/types/error.types';

interface ErrorContextValue {
  /** Current error (if any) */
  error: AppError | null;
  
  /** Show an error */
  showError: (error: unknown, context?: Partial<ErrorContextType>, recovery?: ErrorRecoveryOptions) => void;
  
  /** Clear the current error */
  clearError: () => void;
  
  /** Error history (last 10 errors) */
  errorHistory: AppError[];
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [error, setError] = useState<AppError | null>(null);
  const [errorHistory, setErrorHistory] = useState<AppError[]>([]);
  
  const showError = useCallback((
    errorInput: unknown,
    context?: Partial<ErrorContextType>,
    recovery?: ErrorRecoveryOptions
  ) => {
    const appError = createAppError(errorInput, context);
    appError.recovery = recovery;
    
    setError(appError);
    
    // Add to history (keep last 10)
    setErrorHistory(prev => [appError, ...prev].slice(0, 10));
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Application Error:', appError);
    }
  }, []);
  
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  return (
    <ErrorContext.Provider value={{ error, showError, clearError, errorHistory }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}
